import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function AdminLayout({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth/login', { replace: true });
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const navItems = useMemo(
    () => [
      { label: 'Dashboard', to: '/admin/dashboard', icon: 'ri-dashboard-line' },
      { label: 'Requests', to: '/admin/requests', icon: 'ri-file-list-3-line' },
      { label: 'Assignments', to: '/admin/assignments', icon: 'ri-handshake-line' },
      { label: 'Financials', to: '/admin/financials', icon: 'ri-money-dollar-circle-line' },
      { label: 'Notifications', to: '/admin/notifications', icon: 'ri-notification-3-line' },
      { label: 'Accounts', to: '/admin/account-management', icon: 'ri-user-settings-line' },
    ],
    []
  );

  const isActive = (to: string) => {
    return location.pathname === to || (to !== '/admin/dashboard' && location.pathname.startsWith(to));
  };

  return (
    <div className="min-h-screen bg-slate-50 text-[16px] md:text-[14px] leading-relaxed md:leading-normal overflow-x-hidden">
      <input id="admin-nav-open" type="checkbox" className="peer hidden" />

      <div className="md:hidden sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="h-14 px-4 flex items-center justify-between">
          <label
            htmlFor="admin-nav-open"
            className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700"
            aria-label="Open menu"
          >
            <i className="ri-menu-line text-xl" />
          </label>

          <div className="min-w-0 flex-1 px-3">
            <div className="truncate text-base font-semibold text-slate-900">{title}</div>
          </div>

          <div className="h-11 w-11" />
        </div>
      </div>

      <label
        htmlFor="admin-nav-open"
        className="fixed inset-0 z-40 bg-black/40 opacity-0 pointer-events-none transition-opacity peer-checked:opacity-100 peer-checked:pointer-events-auto md:hidden"
        aria-label="Close menu"
      />

      <aside
        className={
          'fixed inset-y-0 left-0 z-50 w-[18rem] bg-white border-r border-slate-200 shadow-xl transform -translate-x-full transition-transform peer-checked:translate-x-0 ' +
          'md:translate-x-0 md:w-20 lg:w-64 md:shadow-none'
        }
      >
        <div className="h-14 md:h-16 px-4 md:px-3 lg:px-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-gold to-amber-500 flex items-center justify-center">
              <i className="ri-steering-2-fill text-deepNavy text-xl" />
            </div>
            <div className="lg:block md:hidden">
              <div className="font-bold text-slate-900">JazielDrive</div>
              <div className="text-xs text-slate-500">Admin</div>
            </div>
          </div>

          <label
            htmlFor="admin-nav-open"
            className="md:hidden h-11 w-11 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-xl" />
          </label>
        </div>

        <div className="p-3 lg:p-4">
          <nav className="space-y-1">
            {navItems.map((item) => {
              const active = isActive(item.to);

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  className={
                    'group flex items-center gap-3 rounded-xl px-3 py-3 font-semibold transition-colors ' +
                    (active ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-100')
                  }
                >
                  <span
                    className={
                      'flex h-11 w-11 items-center justify-center rounded-xl text-lg ' +
                      (active ? 'bg-white/15' : 'bg-slate-100 text-slate-700 group-hover:bg-slate-200')
                    }
                  >
                    <i className={item.icon} />
                  </span>
                  <span className="md:hidden lg:inline">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <button
            type="button"
            onClick={handleSignOut}
            className="mt-2 w-full rounded-xl px-3 py-3 font-semibold text-slate-700 hover:bg-slate-100"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="min-w-0 md:pl-20 lg:pl-64">
        {children}
      </main>
    </div>
  );
}
